import * as React from 'react';
import Grid from '@material-ui/core/Grid';
import styled from '@emotion/styled';
import { Magnetic } from '../../styles';
import { css } from '@emotion/core';
import { miFormControlCore } from './styles/Input.styles';
import { CustomAttributeParser, TrimLeadingWhiteSpace } from "../../common";
import { CLEAR_BTN_TEXT } from './constants/InputConstant';
import { TextInputHeader } from './TextInputHeader';
import { RequiredIndicator } from "../required-indicator";
const StyledGrid = styled(Grid) `
	width: 100%;
`;
const StyledTextArea = styled('textarea') `
	${miFormControlCore}
	min-height: 85px;
	max-height: 155px;
	width: 100%;
	resize: none;
	overflow: auto;
	height: ${Magnetic.textareaHeight};
	line-height: ${Magnetic.textareaLineHeight};
	padding: ${Magnetic.textareaPadding};
	box-shadow: ${Magnetic.textareaBoxShadow};
	font-weight: ${Magnetic.fontWeightNormal};
	font-family: ${Magnetic.baseFontFamily};
	font-size: ${Magnetic.fontSize14};
	border: ${Magnetic.baseBorderWidth} ${Magnetic.baseBorderStyle} ${Magnetic.colorNeutral30};

	&:active,
	&:focus {
		${props => props.value && typeof props.value === 'string' && props.value.length === props.maxLength
    ? css `
						border: ${Magnetic.formControlCoreBorderInvalid};
						box-shadow: ${Magnetic.formControlCoreBoxShadowInvalid};
						background-color: ${Magnetic.formControlCoreBackgroundColorDisabled};
				  `
    : null}
	}

	${props => props.isMagnetic2Theme &&
    css `
			&:required {
				background-image: none;

				&:disabled,
				&:focus,
				&:valid {
					background-image: none;
				}
			}
		`}
`;
const StyledSpan = styled('span') `
	color: ${Magnetic.baseFontColor};
	font-size: ${Magnetic.fontSize14};
`;
const StyledSpanDisabled = styled('span') `
	color: ${Magnetic.disabledFontColor};
`;
const StyledAlertGrid = styled(Grid) `
	height: 24px;
`;
const StyledCharacterLimitAlert = styled(Grid) `
	font-size: ${Magnetic.fontSize14};
	color: ${Magnetic.uamAlertColor};
`;
const StyledAlertIcon = styled('span') `
	padding-right: ${Magnetic.spacer04};
`;
const StyledMagnetic2Header = styled('div') `
	display: flex;
	align-items: center;
	padding-bottom: ${Magnetic.spacer04};

	${props => props.isDisabled &&
    css `
			> div {
				filter: invert(70%);
			}

			> label {
				color: ${Magnetic.disabledFontColor};
			}
		`}
`;
const StyledMagnetic2HeaderLabel = styled('label') `
	margin-bottom: 0;
	line-height: normal;
`;
/**
 * @param id Passed in ID for this component
 * @param value existing value to populate textarea
 * @param maxInputLength Limits the maximum number of characters in a message
 * @param showRemainingCharAt Sets at what value the character counter pops up
 * @param placeHolder Displayed as placeholder text in the message box
 * @param isDisabled boolean to display as disabled
 * @param required boolean to show required indicator
 * @param onChange Method to handle user typed entry
 * @param onBlur Method to handle updating message text
 * @param isClearBtnRequired boolean to display Clear Button
 * @param variableOptions Passed in DropDown for this component, we are always showing default value as Zero index
 * @param textLabel Passed in TextBox HeadingSection for this component
 * @param translateFn [Optional] Callback for translation function
 * @param remainingCharLabel [Optional] Shows the remaining characters label with remaining characters value as per maxInputLength
 * @param groupVariableOptions [Optional] Passed in DropDown(Add Group variables) for this Component, we are always showing default value as Zero index
 */
export const TextInput = (props) => {
    const { useState, useEffect } = React;
    const [characterCount, setCharacterCount] = useState(0);
    const [textValue, setTextValue] = useState(props.value);
    const [characterLimitReachedAlert, setCharacterLimitReachedAlert] = useState(false);
    useEffect(() => {
        setTextValue(props.value);
        setCharacterCount(props.value ? props.value.length : 0);
        setCharacterLimitReachedAlert(handleCharacterLimit(props.value));
    }, [props.value]);
    useEffect(() => {
        setCharacterLimitReachedAlert(handleCharacterLimit(props.value));
    }, [props.maxInputLength]);
    const handleCharacterLimit = (valueOfText) => {
        return valueOfText ? valueOfText.length >= props.maxInputLength : false;
    };
    const handleChangeText = (event) => {
        /* istanbul ignore next - textarea value is always defined per HTML spec */
        let value = event.target.value || '';
        if (props.shouldTrimLeadingWhitespace) {
            value = TrimLeadingWhiteSpace(value);
        }
        setCharacterCount(value.length);
        setTextValue(value);
        props.onChange(value);
        // Show alert message if character limit reached
        setCharacterLimitReachedAlert(handleCharacterLimit(value));
    };
    const getCharacterCountAndRemainingCharLabel = () => {
        const remaining = props.maxInputLength - characterCount;
        return !!props.remainingCharLabel
            ? `${props.remainingCharLabel}: ${remaining}`
            : `${characterCount}/${props.maxInputLength}`;
    };
    const getRemainingCharacters = () => {
        if (props.maxInputLength - characterCount <= props.showRemainingCharAt) {
            if (props.isDisabled) {
                return (React.createElement(StyledSpanDisabled, { id: props.id + '-remaining-characters' }, getCharacterCountAndRemainingCharLabel()));
            }
            else {
                return (React.createElement(StyledSpan, { id: props.id + '-remaining-characters' }, getCharacterCountAndRemainingCharLabel()));
            }
        }
        return null;
    };
    const remainingCharacters = getRemainingCharacters();
    // setToEnd is necessary for consistent behavior in Chrome and IE11.   Chrome does not do this but IE11 does
    const setToEnd = (event) => {
        const temp_value = event.target.value;
        event.target.value = '';
        event.target.value = temp_value;
    };
    const handleBlurText = (event) => {
        if (props.onBlur) {
            props.onBlur(event.target.value);
        }
    };
    const onVariableChange = (selectedOption) => {
        if (textValue &&
            textValue.length > 0 &&
            `${textValue} [${selectedOption.label}] `.length <= props.maxInputLength) {
            setCharacterCount(`${textValue} [${selectedOption.label}] `.length);
            setTextValue(`${textValue} [${selectedOption.label}] `);
            props.onChange(`${textValue} [${selectedOption.label}]`);
        }
        if (!textValue && `[${selectedOption.label}]`.length <= props.maxInputLength) {
            setCharacterCount(`[${selectedOption.label}]`.length);
            setTextValue(`[${selectedOption.label}]`);
            props.onChange(`[${selectedOption.label}]`);
        }
    };
    const onGroupVariableChange = (selectedOption) => {
        if (textValue &&
            textValue.length > 0 &&
            `${textValue} ${selectedOption.value} `.length <= props.maxInputLength) {
            setCharacterCount(`${textValue} ${selectedOption.value} `.length);
            setTextValue(`${textValue} ${selectedOption.value} `);
            props.onChange(`${textValue} ${selectedOption.value}`);
        }
        if (!textValue && `${selectedOption.value}`.length <= props.maxInputLength) {
            setCharacterCount(`${selectedOption.value}`.length);
            setTextValue(`${selectedOption.value}`);
            props.onChange(`${selectedOption.value}`);
        }
    };
    const onClearClick = (event) => {
        event.preventDefault();
        setCharacterCount(0);
        setTextValue('');
        props.onChange('');
    };
    return (React.createElement(StyledGrid, { container: true, direction: 'row' },
        props.isMagnetic2Theme ? (React.createElement(StyledMagnetic2Header, { id: props.id + '-header', isDisabled: props.isDisabled },
            props.required && (React.createElement(RequiredIndicator, { fulfilled: props.isDisabled || !!(textValue === null || textValue === void 0 ? void 0 : textValue.trim().length) })),
            React.createElement(StyledMagnetic2HeaderLabel, { htmlFor: props.id + '-area' }, props.textLabel))) : (React.createElement(TextInputHeader, { isDisabled: props.isDisabled, label: props.textLabel, id: props.id, isClearBtnRequired: props.isClearBtnRequired, variableOptions: props.variableOptions, translateFn: () => props.translateFn(CLEAR_BTN_TEXT), onClearClick: onClearClick, onVariableChange: onVariableChange, groupVariableOptions: props.groupVariableOptions, onGroupVariableChange: onGroupVariableChange })),
        React.createElement(Grid, { container: true, direction: 'row' },
            React.createElement(StyledTextArea, Object.assign({ id: props.id + '-area', value: textValue, maxLength: props.maxInputLength, placeholder: props.placeHolder, disabled: props.isDisabled, required: props.required, autoFocus: props.autoFocus, onChange: handleChangeText, onFocus: setToEnd, onBlur: handleBlurText, style: props.style, isMagnetic2Theme: props.isMagnetic2Theme }, CustomAttributeParser(props.customAttributes)))),
        React.createElement(StyledAlertGrid, { container: true, direction: 'row' },
            characterLimitReachedAlert && (React.createElement(StyledCharacterLimitAlert, { item: true, xs: 10, container: true, direction: "row", alignItems: "center", id: props.id + '-characterLimitAlert' },
                React.createElement(StyledAlertIcon, { className: "mi-icon-alert-filled-triangle" }),
                React.createElement("span", null, props.translateFn('Character limit reached')))),
            React.createElement(Grid, { item: true, xs: characterLimitReachedAlert ? 2 : 12, container: true, direction: "column", alignItems: "flex-end" }, remainingCharacters))));
};
TextInput.defaultProps = {
    translateFn: (key) => key
};
//# sourceMappingURL=TextInput.js.map